import FeatureFlag, { FeatureFlagFacility } from "./FeatureFlag";

/**
 * Flip to true to work against generated dummy data instead of the real
 * Platform Core API (via the Command Center backend at api/FeatureFlag).
 */
const USE_DUMMY_DATA = false;

/**
 * Gates definition-level writes (PATCH enabled / DELETE the flag itself).
 * Tenant-scoped assignment deletes and SetTenantFeatureFlags are live.
 *
 * Definition enable/delete are held: this page is tenant-scoped, so its
 * switches and remove actions target ConditionalFeatureFlagTenant (and that
 * tenant's facility rows), not ConditionalFeatureFlag.Enabled / DELETE {id}.
 * When those ship, they belong on a global (unscoped) flag view — not on
 * this list — because PATCH/DELETE of the definition affects every tenant.
 * The CC proxy (Update / Delete) and Platform Core endpoints already exist;
 * the remaining work is UI placement, confirm copy, and gating this flag on.
 */
export const WRITE_ACTIONS_ENABLED = false;

/**
 * Wire shape of one facility entry inside a GetForTenant row. Matches the
 * fields the UI reads from the backend's ConditionalFeatureFlagFacilityDetail
 * (one ConditionalFeatureFlagFacility row); other fields are ignored.
 */
interface ConditionalFeatureFlagFacilityDetail {
  /** ConditionalFeatureFlagFacility row id. */
  id: number;
  /** Id of the facility the flag applies to. */
  facilityId: number;
  /** Display name of the facility the flag applies to. */
  facilityName: string;
  /** ISO date the flag started (or starts) applying to the facility. */
  startDateTime: string | null;
  /** ISO date the flag stops applying to the facility, if an end is set. */
  endDateTime: string | null;
}

/**
 * Wire shape of one api/FeatureFlag/GetForTenant row: a flag definition joined
 * with the selected tenant's ConditionalFeatureFlagTenant row. Matches the
 * backend's ConditionalFeatureFlagTenantDetail; the endpoint returns a plain
 * array of these.
 */
interface ConditionalFeatureFlagTenantDetail {
  /**
   * ConditionalFeatureFlagTenant row id, or null when the flag applies to the
   * tenant only through facility rows.
   */
  id: number | null;
  /** Id of the flag definition. */
  conditionalFeatureFlagId: number;
  /** Id of the tenant the flag applies to. */
  tenantId: number;
  /** Display name of the tenant the flag applies to. */
  tenantName: string;
  /** Name of the flag definition. */
  flagName: string;
  /** Description of the flag definition, if any. */
  flagDescription: string | null;
  /** Whether the flag is currently enabled for this tenant. */
  flagEnabled: boolean;
  /** ISO date the flag definition was created. */
  flagCreated: string | null;
  /** ISO date the flag definition was last modified, if ever. */
  flagModified: string | null;
  /** ISO date the flag started (or starts) applying to the tenant. */
  startDateTime: string | null;
  /** ISO date the flag stops applying to the tenant, if an end is set. */
  endDateTime: string | null;
  /** This tenant's facilities the flag applies to. */
  facilities: ConditionalFeatureFlagFacilityDetail[] | null;
}

/**
 * Thrown when the backend reports the saved Platform Core access token has
 * expired (401 with reason "platformCoreTokenExpired"). The page offers the
 * user a re-authentication round trip instead of showing a generic error.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super("Your Platform Core session has expired.");
    this.name = "SessionExpiredError";
  }
}

/** Parses an ISO date string to a Date; null stays null. */
function toDate(value: string | null): Date | null {
  return value ? new Date(value) : null;
}

/**
 * Whether a flag<=>tenant or flag<=>facility row is active right now: its
 * start date (if any) has passed and its end date (if any) has not.
 * SetTenantFeatureFlags disables a row by setting its end date to now and
 * enables it by clearing the end date.
 */
export function isRowActive(startDate: Date | null, endDate: Date | null): boolean {
  const now = Date.now();
  return (
    (startDate == null || startDate.getTime() <= now) &&
    (endDate == null || endDate.getTime() > now)
  );
}

/** Converts a wire facility entry to the UI model (ISO strings to Dates). */
function toFacility(item: ConditionalFeatureFlagFacilityDetail): FeatureFlagFacility {
  return {
    id: item.id,
    facilityId: item.facilityId,
    facilityName: item.facilityName,
    startDate: toDate(item.startDateTime),
    endDate: toDate(item.endDateTime),
  };
}

/** Converts a wire GetForTenant row to the UI FeatureFlag model. */
function toFeatureFlag(row: ConditionalFeatureFlagTenantDetail): FeatureFlag {
  return {
    id: row.conditionalFeatureFlagId,
    conditionalFeatureFlagTenantId: row.id,
    name: row.flagName,
    description: row.flagDescription ?? "",
    enabled: row.flagEnabled,
    startDate: toDate(row.startDateTime),
    endDate: toDate(row.endDateTime),
    createdDate: toDate(row.flagCreated),
    lastModifiedDate: toDate(row.flagModified),
    tenantName: row.tenantName,
    facilities: (row.facilities ?? []).map(toFacility),
  };
}

/**
 * Loads the flags that apply to one tenant, already scoped server-side: each
 * result is a flag joined with that tenant's ConditionalFeatureFlagTenant row
 * and facilities.
 *
 * @param tenantId Tenant selected in the left-rail list.
 * @returns The tenant's flags; empty when the tenant has none.
 * @throws {SessionExpiredError} When the Platform Core token has expired.
 * @throws {Error} With a user-displayable message on any other failure.
 */
export async function getFeatureFlags(tenantId: number): Promise<FeatureFlag[]> {
  if (USE_DUMMY_DATA) {
    return GetDummyData(tenantId);
  }

  const response = await fetch(`api/FeatureFlag/GetForTenant/${tenantId}`);
  if (!response.ok) {
    throw await toApiError(response, `Failed to load feature flags (${response.status} ${response.statusText})`);
  }

  const body: ConditionalFeatureFlagTenantDetail[] = await response.json();
  return (body ?? []).map(toFeatureFlag);
}

/**
 * Deletes a feature flag definition and all of its tenant and facility rows.
 *
 * @param id Id of the flag definition to delete.
 * @throws {SessionExpiredError} When the Platform Core token has expired.
 * @throws {Error} With a user-displayable message on any other failure.
 */
export async function deleteFeatureFlag(id: number): Promise<void> {
  if (USE_DUMMY_DATA) {
    return DummyDelete(id);
  }

  const response = await fetch(`api/FeatureFlag/Delete/${id}`, { method: "DELETE" });
  if (!response.ok) {
    throw await toApiError(response, `Failed to delete feature flag ${id} (${response.status})`);
  }
}

/**
 * Deletes the flag<=>tenant row (ConditionalFeatureFlagTenant) that ties one
 * feature flag to one tenant. The flag definition and any facility rows are
 * not affected. Command Center injects the session user id for the audit
 * trail; the browser only sends the flag and tenant ids.
 *
 * @param conditionalFeatureFlagId Id of the flag definition.
 * @param tenantId Tenant whose row is removed.
 * @throws {SessionExpiredError} When the Platform Core token has expired.
 * @throws {Error} With a user-displayable message on any other failure.
 */
export async function removeTenantFeatureFlag(
  conditionalFeatureFlagId: number,
  tenantId: number
): Promise<void> {
  const response = await fetch(
    `api/FeatureFlag/RemoveTenant/${conditionalFeatureFlagId}/${tenantId}`,
    { method: "DELETE" }
  );
  if (!response.ok) {
    throw await toApiError(
      response,
      `Failed to remove feature flag ${conditionalFeatureFlagId} from tenant ${tenantId} (${response.status})`
    );
  }
}

/**
 * Deletes the flag<=>facility row (ConditionalFeatureFlagFacility) that ties
 * one feature flag to one facility. The flag definition and any other rows
 * are not affected. Command Center injects the session user id for the audit
 * trail; the browser only sends the flag and facility ids.
 *
 * @param conditionalFeatureFlagId Id of the flag definition.
 * @param facilityId Facility whose row is removed (facility id, not the row id).
 * @throws {SessionExpiredError} When the Platform Core token has expired.
 * @throws {Error} With a user-displayable message on any other failure.
 */
export async function removeFacilityFeatureFlag(
  conditionalFeatureFlagId: number,
  facilityId: number
): Promise<void> {
  const response = await fetch(
    `api/FeatureFlag/RemoveFacility/${conditionalFeatureFlagId}/${facilityId}`,
    { method: "DELETE" }
  );
  if (!response.ok) {
    throw await toApiError(
      response,
      `Failed to remove feature flag ${conditionalFeatureFlagId} from facility ${facilityId} (${response.status})`
    );
  }
}

/**
 * The updated flag<=>tenant row returned by SetTenantFeatureFlags, parsed to
 * Dates. Mirrors the ConditionalFeatureFlagTenant table row.
 */
export interface ConditionalFeatureFlagTenant {
  /** ConditionalFeatureFlagTenant row id. */
  id: number;
  /** Id of the tenant the flag applies to. */
  tenantId: number;
  /** When the flag started (or starts) applying to the tenant. */
  startDate: Date | null;
  /**
   * When the flag stops applying to the tenant; null or a future date means
   * the row is currently enabled.
   */
  endDate: Date | null;
}

/**
 * Ends or restarts a feature flag for one tenant by updating its
 * ConditionalFeatureFlagTenant row. Command Center injects the session user
 * name; the browser only sends flag id, tenant id, and enabled.
 *
 * @param conditionalFeatureFlagId Id of the flag definition.
 * @param tenantId Tenant whose row is changed.
 * @param enabled True restarts the row; false ends it now.
 * @returns The updated ConditionalFeatureFlagTenant row from Platform Core.
 * @throws {SessionExpiredError} When the Platform Core token has expired.
 * @throws {Error} With a user-displayable message on any other failure.
 */
export async function setTenantFeatureFlags(
  conditionalFeatureFlagId: number,
  tenantId: number,
  enabled: boolean
): Promise<ConditionalFeatureFlagTenant> {
  const response = await fetch("api/FeatureFlag/SetTenantFeatureFlags", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conditionalFeatureFlagId, tenantId, enabled }),
  });

  if (!response.ok) {
    throw await toApiError(
      response,
      `Failed to update feature flag ${conditionalFeatureFlagId} for tenant ${tenantId} (${response.status})`
    );
  }

  const body: {
    id: number;
    tenantId: number;
    startDateTime: string | null;
    endDateTime: string | null;
  } = await response.json();

  return {
    id: body.id,
    tenantId: body.tenantId,
    startDate: toDate(body.startDateTime),
    endDate: toDate(body.endDateTime),
  };
}

/**
 * Applies an updated ConditionalFeatureFlagTenant row (from a
 * SetTenantFeatureFlags response) onto the selected-tenant flag, rederiving
 * enabled from the row's date window.
 *
 * @param flag The flag as currently held by the page.
 * @param conditionalFeatureFlagTenant The updated row returned by the server.
 * @returns A new flag carrying the row's id, dates, and derived enabled state.
 */
export function applyConditionalFeatureFlagTenant(
  flag: FeatureFlag,
  conditionalFeatureFlagTenant: ConditionalFeatureFlagTenant
): FeatureFlag {
  return {
    ...flag,
    conditionalFeatureFlagTenantId: conditionalFeatureFlagTenant.id,
    enabled: isRowActive(
      conditionalFeatureFlagTenant.startDate,
      conditionalFeatureFlagTenant.endDate
    ),
    startDate: conditionalFeatureFlagTenant.startDate,
    endDate: conditionalFeatureFlagTenant.endDate,
  };
}

/**
 * Applies a successful RemoveTenantFeatureFlag onto the selected-tenant flag,
 * mirroring what GetForTenant would return after the delete: with facility
 * rows left the flag becomes facility-only (null row id, dates cleared,
 * enabled derived from the facility rows); with none the flag no longer
 * applies to the tenant at all.
 *
 * @param flag The flag as currently held by the page.
 * @returns The facility-only flag, or null when the flag should be dropped
 * from the tenant's list.
 */
export function removeConditionalFeatureFlagTenant(flag: FeatureFlag): FeatureFlag | null {
  if (flag.facilities.length === 0) {
    return null;
  }

  return {
    ...flag,
    conditionalFeatureFlagTenantId: null,
    enabled: flag.facilities.some((facility) =>
      isRowActive(facility.startDate, facility.endDate)
    ),
    startDate: null,
    endDate: null,
  };
}

/**
 * Applies a successful RemoveFacilityFeatureFlag onto the selected-tenant
 * flag, mirroring what GetForTenant would return after the delete: the
 * facility row disappears; a facility-only flag rederives enabled from the
 * remaining facility rows and drops from the tenant's list when none are
 * left. A flag with a tenant row keeps its tenant-derived enabled state.
 *
 * @param flag The flag as currently held by the page.
 * @param facilityId Facility whose row was removed (facility id, not the row id).
 * @returns The flag without the facility row, or null when the flag should
 * be dropped from the tenant's list.
 */
export function removeConditionalFeatureFlagFacility(
  flag: FeatureFlag,
  facilityId: number
): FeatureFlag | null {
  const facilities = flag.facilities.filter(
    (facility) => facility.facilityId !== facilityId
  );

  if (flag.conditionalFeatureFlagTenantId == null && facilities.length === 0) {
    return null;
  }

  return {
    ...flag,
    facilities,
    enabled:
      flag.conditionalFeatureFlagTenantId != null
        ? flag.enabled
        : facilities.some((facility) => isRowActive(facility.startDate, facility.endDate)),
  };
}

/**
 * Maps an error response to the error to throw. An expired-token 401 (marker
 * from RequirePlatformCoreTokenAttribute) becomes SessionExpiredError; other
 * Platform Core errors are ProblemDetails whose "detail" is user-displayable,
 * with the provided generic message as fallback for non-JSON bodies.
 */
async function toApiError(response: Response, fallback: string): Promise<Error> {
  let body: { reason?: string; detail?: string } | null = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON error body; keep the generic message.
  }

  if (response.status === 401 && body?.reason === "platformCoreTokenExpired") {
    return new SessionExpiredError();
  }

  return new Error(body?.detail ?? fallback);
}

async function DummyDelete(id: number): Promise<void> {
  if (Math.random() < 0.5) {
    throw new Error(`Simulated delete failure for feature flag ${id}`);
  }
}

async function GetDummyData(tenantId: number): Promise<FeatureFlag[]> {
  const tenantName = `Tenant ${tenantId}`;

  return [
    {
      id: 1,
      conditionalFeatureFlagTenantId: 101,
      name: "test1",
      description: "Short description",
      startDate: new Date("2026-07-14T00:00:00.000-04:00"),
      endDate: null,
      enabled: true,
      createdDate: new Date("2026-06-01T08:00:00.000-04:00"),
      lastModifiedDate: new Date("2026-07-10T14:30:00.000-04:00"),
      tenantName,
      facilities: [
        {
          id: 1,
          facilityId: 2000,
          facilityName: "Facility A",
          startDate: new Date("2026-08-01T00:00:00.000-04:00"),
          endDate: null,
        },
      ],
    },
    {
      id: 2,
      conditionalFeatureFlagTenantId: 102,
      name: "test2",
      description: "This is a very long description. It goes on for a while. There are details, instructions, and other information. It needs to be truncated or wrapped or both else the table will break",
      startDate: new Date("2026-07-14T00:00:00.000-04:00"),
      endDate: new Date("2026-07-18T20:00:00.000-04:00"),
      enabled: false,
      createdDate: new Date("2026-05-15T09:15:00.000-04:00"),
      lastModifiedDate: null,
      tenantName,
      facilities: [],
    },
    {
      id: 3,
      conditionalFeatureFlagTenantId: 103,
      name: "test3",
      description: "Medium description, clarifying",
      startDate: new Date(),
      endDate: null,
      enabled: true,
      createdDate: new Date("2026-07-01T10:00:00.000-04:00"),
      lastModifiedDate: new Date(),
      tenantName,
      facilities: [],
    },
  ];
}
